import 'react-toastify/dist/ReactToastify.css'
import '@containers/admin/css/AdminPickCalendar.css'
import '@css/utilities.css'

import useI18nApi from '@api/useI18nApi'
import IconScreenRotation from '@assets/images/icons/icon_screen_rotation.png'
import { getOvalModeOffsets, kioskModeActive, ovalModeActive, previewActive, replayModeActive, tvModeActive
} from '@classes/app_url_params'
import type { RemoteControlMessage } from '@classes/RemoteControl'
import RemoteControl from '@classes/RemoteControl'
import WindyLoader from '@classes/windy_loader'
import Dashboard from '@components/Dashboard/Dashboard'
import { checkIfWindyIsEnabled } from '@components/Maps/mapOptions'
import Menu from '@components/Menu/Menu'
import OvalGaugeCluster from '@components/OvalDashboard/OvalGaugeCluster'
import {
  loadAndStoreConfig,
  loadAndStorePagesConfig,
  MAIN_ADMIN_ROUTE,
  setupDefaultPages,
  setupPages
} from '@config'
import { ADMIN_ROUTES, ROUTES } from '@constants/app_routes'
import { pagesUnderDashboard, pagesUnderOvalDashboard, pagesWithoutFooter } from '@constants/routes_definitions'
import { hasOvalDashboard, shouldClearScrollLocks, shouldShowDashboard } from '@constants/routes_helpers'
import { toastError, toastSuccess } from '@constants/toasts'
import type { AppState } from '@constants/types'
import { getSideBarActiveGauges, TemperatureUnit } from '@constants/utils'
import useApi, { getServerUrl } from '@hooks/useApi'
import { useYisNavigate } from '@hooks/useYisNavigate'
import Logger from '@logger'
import { CircularProgress, Typography } from '@mui/material'
import Grid from '@mui/material/Grid'
import { type NullableString } from '@shared_types/global_types'
import { isVesselTime, TimeModels } from '@shared_utils/time_utils'
import { load, updateAuth, updateOvalMode, updateTime, updateTvMode, updateUnit } from '@store/actions'
import type { TempUnitSocket } from '@store/reducers/units'
import { adminPrefix } from '@utils/navigation_utils'
import { clearAllBodyScrollLocks } from 'body-scroll-lock'
import isIPad from 'is-ios'
import TimeAgo from 'javascript-time-ago'
import en from 'javascript-time-ago/locale/en.json'
import useRealTimeClock from '@hooks/useRealTimeClock'
import { getTempUnit, setNumberFormatLocale } from '@utils/unit_utils'
import { get, isString, zipObject } from 'lodash'
import type { Moment } from 'moment'
import moment from 'moment-timezone'
import type { CSSProperties } from 'react'
import React, { useEffect, useRef, useState } from 'react'
import { getI18n, useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { io } from 'socket.io-client'
import Cookies from 'universal-cookie'

import { reloadWindow } from './app_globals'
import ToastContainer from './components/StyledToastContainer'
import Routes from './Routes'
import BackgroundCanvas from './theme/BackgroundCanvas'
import { ThemeProvider } from './theme/ThemeProvider'

TimeAgo.addDefaultLocale(en)

const logger = Logger.create('app')

const MAX_MEM = 400 * 1024 * 1024 // 400 MB

export type AuthPayload = {
  name: string
  value: boolean
}

interface LocationState {
  hideMenu: boolean
}

const App = () => {
  const { t, i18n } = useTranslation()
  const { apiGet } = useApi()
  const { upsertTranslation } = useI18nApi()
  const tvMode = tvModeActive() || previewActive()
  const ovalMode = useSelector((appState: AppState) => appState.ovalMode)
  const isOvalMode = ovalModeActive()
  const dispatch = useDispatch()

  const navigate = useYisNavigate()
  const location = useLocation()
  const locationState = location.state as LocationState
  const [configIsLoaded, setConfigIsLoaded] = useState(false)
  const [orientation, setOrientation] = useState(window.innerHeight > window.innerWidth ? 'portrait' : 'landscape')
  const [loadedCss, setLoadedCss] = useState(false)
  const [canSetBackground, setCanSetBackground] = useState(false)
  const timeModel = useRef<{ fetched: boolean; value: NullableString }>({
    fetched: false,
    value: null
  })
  const { synced, rtc, rtcTs } = useRealTimeClock()

  const cookies = new Cookies(null, { path: '/' })

  const updateRealTimeClock = () => {
    const tm = timeModel.current.value
    if (!isString(tm) || !timeModel.current.fetched) {
      return
    }

    let newTime: Moment | null = null

    if (isVesselTime(tm)) {
      if (synced.current) {
        logger.debug(`[Time] use vessel time (${rtc.current})`)
        newTime = moment(rtc.current) // force reference update
      } else {
        logger.debug('[Time] cannot yet use vessel time (not synced)')
      }
    } else if (tm === TimeModels.LOCAL_TIME || !tm) {
      logger.debug('[Time] use browser time')
      newTime = moment()
    } else {
      logger.debug(`[Time] use fixed timezone (${tm})`)
      try {
        newTime = moment().tz(tm)
      } catch (e) {
        logger.error(`[Time] invalid timezone ${tm}`, e)
        newTime = null
      }
    }

    if (newTime) {
      dispatch(updateTime(newTime, tm))
    }
  }

  const { fetchTranslations: i18nFetchTranslations, getEffectiveLanguageCode } = useI18nApi()
  const { LDtop, LDleft, RDtop, RDleft } = getOvalModeOffsets()
  const didLoadI18n = useRef(false)

  const activeGauges = getSideBarActiveGauges()

  const pathname = location.pathname as ROUTES

  if (tvMode && !isOvalMode && !isIPad) {
    const isTvModePlayer = cookies.get<boolean>('isTvModePlayer')

    if (!isTvModePlayer) {
      cookies.set('isTvModePlayer', true)
    }
  } else {
    cookies.remove('isTvModePlayer')
  }

  const remoteControl = RemoteControl.getInstance({ location, navigate }, socket)
  let countInterval: NodeJS.Timeout
  let lastInterval = Date.now()

  const { language } = i18n

  moment.locale(language)
  setNumberFormatLocale(language)

  const startedAt: Date = new Date()

  socket.on('error.modbus', (message: string) => toastError(message))

  socket.on('info.modbus', (message: string) => toastSuccess(message))

  socket.on('units.tempUnit', (o: TempUnitSocket) => {
    dispatch(updateUnit('TEMP_UNIT', o.tempUnit))
    dispatch(updateUnit('TEMP_UNIT_GLOBAL', o.tempUnit))
  })

  const handleVisibilityChange = () => {
    if (document.visibilityState === 'hidden') {
      logout()
    }
  }

  const intervalHeartbeat = () => {
    const now = Date.now()
    const offBy = now - lastInterval - 1000
    lastInterval = now

    if (offBy > 50 && document.visibilityState === 'hidden') {
      logout()
    }
  }

  const handleResize = () => {
    // First we get the viewport height, and we multiply it by 1% to get a value for a vh unit
    const vh = window.innerHeight * 0.01

    // Then we set the value in the --vh custom property to the root of the document
    document.documentElement.style.setProperty('--vh', `${vh}px`)

    setOrientation(window.innerHeight > window.innerWidth ? 'portrait' : 'landscape')
  }

  const clearLogoutStorage = () => {
    localStorage.removeItem('pincode')
    localStorage.removeItem('expiresIn')
  }

  const logout = () => {
    dispatch(updateAuth('auth', { name: 'isAuthenticated', value: false }))
    clearLogoutStorage()
  }

  const fetchTranslations = (lang: string) => {
    i18nFetchTranslations(lang).then(async translations => {
      const keys = translations.map(t => t.key)
      const values = translations.map(t => t.translation)
      i18n.addResources(lang, 'translations', zipObject(keys, values))
      moment.locale(lang)
      setNumberFormatLocale(lang)
      await i18n.changeLanguage(lang)
      logger.verbose(`language changed to ${lang}`)
      didLoadI18n.current = true
    })
  }

  const fetchEnabledLanguages = () => {
    const userSelectedLanguage = localStorage.getItem('language')
    getEffectiveLanguageCode(userSelectedLanguage || '').then(({ code }) => {
      if (code) {
        fetchTranslations(code)
      } else {
        logger.warn('lang code not known')
      }
    })
  }

  const updateTimeModel = () => {
    apiGet<string>('api/timeModel')
      .then(tm => {
        if (isString(tm)) {
          timeModel.current.value = tm
        }
        timeModel.current.fetched = true
      })
      .catch(err => {
        logger.error('Could not load time model', err as Error)
      })
  }

  const setup = async () => {
    await loadAndStoreConfig()

    setLoadedCss(true)

    const isValidCode = await apiGet<{ code: string }>('api/pincode')
    dispatch(updateAuth('auth', { name: 'showAuthForm', value: !!isValidCode.code }))

    // only allow text selection in admin panel
    document.body.onselectstart = () => pathname.includes('admin')

    updateTimeModel()
    setInterval(updateTimeModel, 13000)

    const isWindyEnabled = checkIfWindyIsEnabled()

    const i18nInstance = getI18n()

    i18nInstance.on('missingKey', function (lngs, ns, key, translation) {
      const moreVerbose = Math.random() > 1
      if (moreVerbose) {
        console.log('missingKey', { lngs, ns, key, translation })
      }

      if (!didLoadI18n.current || !key) {
        return
      }

      for (const code of lngs) {
        i18nInstance.addResource(code, ns, key, translation)
        upsertTranslation({ key, code, translation }).then(() => {
          if (moreVerbose) {
            console.log('new i18n key added')
          }
        })
      }
    })

    if (tvMode || isOvalMode) {
      if (!isIPad) {
        setInterval(() => {
          const rootDiv = document.getElementById('root')
          if (rootDiv && !rootDiv.childNodes.length) {
            reloadWindow('no root')
          }

          const usedMem = +get(window, 'performance.memory.usedJSHeapSize', 0)
          const totalMem = +get(window, 'performance.memory.totalJSHeapSize', 0)

          const usedMB = Math.round(usedMem / (1024 * 1024))
          const totalMB = Math.round(totalMem / (1024 * 1024))

          logger.debug(`memory: used=${usedMB} MB, total=${totalMB} MB`)

          if (usedMem > 0 && totalMem > 0) {
            const ratio1 = usedMem / MAX_MEM
            const ratio2 = usedMem / totalMem
            if (ratio1 < 1 && ratio2 < 1 && (ratio1 > 0.95 || ratio2 > 0.99)) {
              reloadWindow(`memory alert [ratio1=${ratio1} ratio2=${ratio2}]`)
            }
          }

          socket.emit('browser-report', {
            usedMB,
            totalMB,
            pathname,
            started: startedAt
          })
        }, 5000)
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    countInterval = setInterval(intervalHeartbeat, 1000)

    socket.on('remote-control', (message: RemoteControlMessage) => {
      if (tvMode && !replayModeActive()) {
        remoteControl.commandChanged(message.command)
      } else {
        console.log(`got remote control command ${message.command}, but will ignore - is not in TV Mode`)
      }
    })

    fetchEnabledLanguages()

    // at this moment application is ready to start animate background
    setCanSetBackground(true)

    dispatch(load('main/position'))

    document.querySelector('html')?.classList.toggle('tv-mode', tvMode)

    dispatch(updateTvMode('TV_MODE', tvMode))
    dispatch(updateOvalMode('OVAL_MODE', isOvalMode))

    if (isWindyEnabled) {
      try {
        await WindyLoader.loadWindy(window, 'app init')
      } catch (e) {
        logger.error(`got error: ${e}`)
        toastError('Configuration file is required')
      }
    }

    setConfigIsLoaded(true)

    // set unit temperature
    const localTempUnit = getTempUnit()
    if (localTempUnit) {
      dispatch(updateUnit('TEMP_UNIT', localTempUnit))
      dispatch(updateUnit('TEMP_UNIT_GLOBAL', localTempUnit))
    } else {
      loadAndStoreConfig().then(value => {
        const defaultTempUnit = get(value, 'model.temperature.tempUnit', TemperatureUnit.CELSIUS)
        dispatch(updateUnit('TEMP_UNIT', defaultTempUnit))
        dispatch(updateUnit('TEMP_UNIT_GLOBAL', defaultTempUnit))
      })
    }

    handleResize()
    window.addEventListener('resize', handleResize)
    remoteControl.start()
  }

  useEffect(() => {
    setupDefaultPages().catch(e => {
      logger.error(`unable to setup default pages: ${e.message}`)
    })

    setupPages().catch(e => {
      logger.error(`unable to setup pages: ${e.message}`)
    })

    setup().catch(e => {
      logger.error(`unable to run setup: ${e.message}`)
    })
  }, [])

  useEffect(() => {
    return () => {
      window.removeEventListener('resize', handleResize)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      clearInterval(countInterval)

      if (shouldClearScrollLocks(pathname)) {
        clearAllBodyScrollLocks()
      }

      remoteControl.stop()
      logout()
    }
  }, [])

  useEffect(() => {
    if (synced.current) {
      updateRealTimeClock()
    }
  }, [timeModel.current.value, synced.current, rtcTs.current])

  useEffect(() => {
    const updateClockId = setInterval(updateRealTimeClock, 1333)

    return () => {
      clearInterval(updateClockId)
    }
  }, [])

  useEffect(() => {
    // @ts-expect-error TODO types error
    if (pathname === MAIN_ADMIN_ROUTE) {
      navigate(adminPrefix(ADMIN_ROUTES.ADMIN_SETTINGS))
      // @ts-expect-error TODO types error
    } else if (pathname === '/') {
      loadAndStorePagesConfig().then(pages => {
        const firstEnabledPage = pages.find(page => !page.disabled)
        if (firstEnabledPage) {
          const firstPageKey = firstEnabledPage.key.toUpperCase()
          const firstPagePath = ROUTES[firstPageKey.toUpperCase() as keyof typeof ROUTES]
          if (firstPagePath) {
            navigate(firstPagePath)
            return
          }
        }

        navigate('/home')
      })
    }
  }, [pathname])

  if (orientation === 'portrait') {
    return (
      <div className="app">
        <Grid container className="container-100" sx={{ justifyContent: 'center', alignContent: 'center' }}>
          <Grid container sx={{ flexDirection: 'column', alignContent: 'center' }}>
            <Typography variant="h5">{t('YIS only works in landscape orientation')}</Typography>
            <img className="screen-rotate-image" src={IconScreenRotation} alt="screen_rotation" />
          </Grid>
        </Grid>
      </div>
    )
  }

  if (!loadedCss) {
    return <></>
  }

  const getMainClass = () => {
    return pathname.startsWith(MAIN_ADMIN_ROUTE) ? 'admin' : kioskModeActive() ? 'kiosk' : ''
  }

  const shouldHideMenu = kioskModeActive() || tvMode || !!locationState?.hideMenu || pagesWithoutFooter.includes(pathname)

  document.body.className = getMainClass()

  let isUnderDashboard: boolean

  if (tvMode) {
    isUnderDashboard = pagesUnderDashboard.includes(pathname)
  } else if (isOvalMode) {
    isUnderDashboard = pagesUnderOvalDashboard.includes(pathname)
  } else {
    isUnderDashboard = false
  }

  const isWithDashboard = shouldShowDashboard(pathname)

  const showOvalGui = configIsLoaded && isOvalMode

  const styleAroundRoutes: CSSProperties = {
    width: isUnderDashboard || isOvalMode ? '100%' : isWithDashboard ? '88vw' : '100%',
    height: '100vh',
    position: isUnderDashboard || (isOvalMode && isWithDashboard) ? 'absolute' : 'static'
  }

  const clusterClassName = hasOvalDashboard(pathname) ? 'oval-gauge-cluster ogc-visible' : 'oval-gauge-cluster'

  const leftOvalWrapperStyle: CSSProperties = { width: `${ovalMode.width}` }
  const rightOvalWrapperStyle: CSSProperties = {
    width: `${ovalMode.width}`,
    right: 0
  }

  return <ThemeProvider>
    <div>
      <div id="weather_portal" />

      {!pathname.startsWith('/admin') && (<BackgroundCanvas canSetBackground={canSetBackground} />)}

      <div className="app">
        <main className={getMainClass()}>
          {!configIsLoaded && <Grid
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              color: '#458FB2'
            }}
          >
            <CircularProgress color="inherit" />
          </Grid>}

          {configIsLoaded && <Grid container style={{ height: '100vh', flexWrap: 'nowrap' }}>
            {!showOvalGui && isWithDashboard && <Grid container style={{ width: '12vw' }} className="left-dashboard" sx={{
              justifyContent: 'center'
            }}>
              <Grid container className="left-dashboard-wrapper" style={{ width: '12vw' }} sx={{
                justifyContent: 'center'
              }}>
                <Dashboard
                  showDepth={activeGauges.depth}
                  showOrientation={activeGauges.heading}
                  showPressure={activeGauges.pressure}
                  showSpeed={activeGauges.speed}
                  showTime={activeGauges.time}
                  showWind={activeGauges.wind}
                  showWeather={activeGauges.temperature}
                  showMecca={activeGauges.mekkah}
                  showEarth={activeGauges.location}
                  showEcoScore={activeGauges.eco_score}
                />
              </Grid>
            </Grid>}

            <div style={styleAroundRoutes}>
              <Routes />
            </div>
          </Grid>}
        </main>

        {!shouldHideMenu && <Menu />}

        <ToastContainer position="bottom-center" hideProgressBar toastClassName="toast-container" autoClose={6000} />
      </div>

      {showOvalGui && <div style={{ position: 'absolute', left: 0, top: 0, zIndex: 999 }} className={clusterClassName}>
        <div style={{ position: 'relative', width: '100vw', height: '100vh' }}>
          <div style={leftOvalWrapperStyle} className="oval-dashboard oval-left-dashboard">
            <div style={{ marginTop: LDtop, marginLeft: LDleft }} className="oval-dashboard-wrapper">
              <OvalGaugeCluster side={'left'} />
            </div>
          </div>

          <div style={rightOvalWrapperStyle} className="oval-dashboard oval-right-dashboard">
            <div style={{ marginTop: RDtop, marginLeft: RDleft }} className="oval-dashboard-wrapper">
              <OvalGaugeCluster side={'right'} />
            </div>
          </div>
        </div>
      </div>}
    </div>
  </ThemeProvider>
}

export const socket = io(getServerUrl('', { tvMode: tvModeActive() }))

socket?.on('client-refresh', (message: { clients: string }) => {
  if (window.location.pathname.startsWith('/admin')) {
    console.log('dont reload admin panels')
  } else if (message.clients !== 'tv' || tvModeActive()) {
    reloadWindow('got request from SocketIO to refresh client view')
  }
})

export default App
